/**
 * 블록 → 파이썬(2026-10-08, 직원 김혜정 님 질문 "스크래치 블록코딩 > 파이썬으로 변경하는 기능 있나요?").
 *
 * 지금 스프라이트의 블록을 **파이썬 탭에서 그대로 실행되는 코드**로 바꾼다.
 *   - 로봇 블록 7종은 파이썬 탭 명령과 1:1(turn_dcmotor1 · turnon_colorlight · display_set_pixel · play_note · is_obstacle_detected …)
 *   - 반복·조건·기다리기·변수·연산·말하기(print)·신호(함수 호출)·내 블록(def)
 *   - 바꿀 수 없는 블록(무대 움직임·소리·카메라 AI 등)은 지우지 않고 `# …` 주석으로 남긴다
 *
 * ⚠️ 파이썬 탭 엔진(jspython) 문법 제약(실측):
 *   - 조건 전체를 괄호로 감싸면(`if (…):`, `while (…):`) 파서 오류 → 맨 바깥 괄호를 만들지 않는다
 *   - `not x`·`not f()` 는 파서 오류, `not (x)` 만 된다 → not 뒤에는 늘 괄호
 *   - `global` 이 없다. 함수 안에서 바깥 변수에 넣으면 조용히 함수 안 변수가 된다(밖은 그대로)
 *     → 함수 안에서 바꾸는 변수만 함께 쓰는 사전 `변수["이름"]` 에 넣는다(사전 칸 바꾸기는 밖에도 보인다, 실측)
 *   - `def` 는 실행 직전에 `async def` 로 바뀐다(python-compat.js) — 여기서는 그냥 def
 *   - 파이썬 탭에는 이벤트(키·클릭)가 없다 → 깃발 스크립트는 차례로, 센서 모자 블록은 마지막 `while True` 검사로,
 *     그 밖의 이벤트는 함수로만 만들어 둔다(신호 보내기는 그 함수를 부른다)
 * 시험: node test/blocks-to-python/run.js (실제 VM 블록 → 변환 → jspython 실행)
 */
'use strict';

const T = {
    ko: {
        head: n => `# 블록에서 바꾼 파이썬 코드 (스프라이트: ${n})`,
        headNote: '# 바꿀 수 없는 블록은 # 주석으로 남겼습니다',
        noScripts: '# 바꿀 블록이 없습니다. [코드] 탭에서 블록을 만든 뒤 다시 눌러 주세요.',
        flagMany: '# 스크래치에서는 깃발 스크립트들이 동시에 돌지만, 파이썬에서는 위에서부터 차례로 실행됩니다',
        loopsLast: '# 무한 반복은 끝나지 않으므로 맨 아래 한 반복으로 모았습니다',
        loopFrom: n => `# (깃발 스크립트 ${n})`,
        sensorLoop: '# 센서 이벤트(모자 블록)는 아래 반복에서 계속 확인합니다',
        sensorInLoop: '# 센서 이벤트(모자 블록)는 이 반복 안에서 확인합니다',
        eventFn: label => `# "${label}" 이벤트는 파이썬 탭에 없어서 함수로만 만들었습니다`,
        unsupported: (cat, op) => `# [${cat}] ${op} 블록은 파이썬 탭에 없습니다`,
        unsupportedExpr: op => `${op} 값은 파이썬 탭에 없어서 None 으로 두었습니다`,
        stop: '# 멈추기 블록',
        store: '변수',
        storeNote: '# 함수 안에서 바꾸는 변수는 함께 쓰는 상자(변수["이름"])에 넣었습니다',
        noReceiver: msg => `# "${msg}" 신호를 받는 블록이 없습니다`,
        noProc: name => `# "${name}" 내 블록의 정의가 없습니다`,
        cats: {motion: '동작', looks: '형태', sound: '소리', event: '이벤트', control: '제어', sensing: '감지',
            operator: '연산', data: '변수', procedures: '내 블록', pen: '펜', other: '확장'}
    },
    en: {
        head: n => `# Python converted from blocks (sprite: ${n})`,
        headNote: '# Blocks that cannot be converted are left as # comments',
        noScripts: '# No blocks to convert. Make blocks in the [Code] tab, then press again.',
        flagMany: '# In Scratch the flag scripts run at the same time; in Python they run one after another',
        loopsLast: '# Forever loops never end, so they are gathered into one loop at the bottom',
        loopFrom: n => `# (flag script ${n})`,
        sensorLoop: '# Sensor events (hat blocks) are checked in the loop below',
        sensorInLoop: '# Sensor events (hat blocks) are checked inside this loop',
        eventFn: label => `# The "${label}" event does not exist in the Python tab, so it is only a function`,
        unsupported: (cat, op) => `# [${cat}] ${op} is not available in the Python tab`,
        unsupportedExpr: op => `${op} is not available in the Python tab, so it is None`,
        stop: '# stop block',
        store: 'variables',
        storeNote: '# Variables changed inside functions are kept in a shared box (variables["name"])',
        noReceiver: msg => `# No block receives the "${msg}" message`,
        noProc: name => `# The "${name}" block has no definition`,
        cats: {motion: 'Motion', looks: 'Looks', sound: 'Sound', event: 'Events', control: 'Control', sensing: 'Sensing',
            operator: 'Operators', data: 'Variables', procedures: 'My Blocks', pen: 'Pen', other: 'Extension'}
    }
};

const PY_KEYWORDS = ['and', 'as', 'assert', 'async', 'await', 'break', 'class', 'continue', 'def', 'del', 'elif', 'else', 'except',
    'False', 'finally', 'for', 'from', 'global', 'if', 'import', 'in', 'is', 'lambda', 'None', 'nonlocal', 'not', 'or', 'pass',
    'raise', 'return', 'True', 'try', 'while', 'with', 'yield', 'print', 'sleep', 'range', 'str', 'int', 'len', 'round', 'randint'];

// 소리 블록 메뉴 값('도 C(60)') → 파이썬 play_note 키('DO_60')
// 파이썬 탭 키 이름(sound-block.js 실측): '라'는 RA_(LA 아님)
const SOLFEGE = {도: 'DO', 레: 'RE', 미: 'MI', 파: 'FA', 솔: 'SOL', 라: 'RA', 시: 'SI'};
const noteKey = name => {
    const m = /^(도|레|미|파|솔|라|시)[#b]?\s.*\((\d+)\)\s*$/.exec(String(name).trim());
    return m ? `${SOLFEGE[m[1]]}_${m[2]}` : null;
};

// LED 8×8 블록의 64칸(0/1) → 파이썬 display_set_pixel 의 16자리(확장 lceMatrix8x8Control 과 같은 계산)
const matrixHex = bits => {
    const s = String(bits).replace(/\s/g, '');
    if (!/^[01]{64}$/.test(s)) return null;
    let out = '';
    for (let r = 0; r < 8; r++) {
        const row = s.substr(r * 8, 8).split('').reverse().join('');
        out += parseInt(row, 2).toString(16).toUpperCase().padStart(2, '0');
    }
    return out;
};

const quote = v => `"${String(v).replace(/\\/g, '\\\\').replace(/"/g, '\\"').replace(/\n/g, '\\n')}"`;
const isNum = v => v !== '' && v !== null && typeof v !== 'undefined' && /^\s*-?\d+(\.\d+)?\s*$/.test(String(v));
const lit = v => (isNum(v) ? String(Number(v)) : quote(v));
// 연산 결과를 다른 식 안에 넣을 때만 괄호(맨 바깥 조건에는 괄호를 만들지 않는다 — jspython 제약)
const isAtomic = e => {
    if (/^[\w가-힣.]+$/.test(e) || /^"(?:[^"\\]|\\.)*"$/.test(e) || /^-?\d+(\.\d+)?$/.test(e)) return true;
    // 함수 호출 하나(f(...))인지 — 여는 괄호의 짝이 맨 끝이어야 한다("str(a) + str(b)" 는 아님)
    const m = /^[\w가-힣.]+\(/.exec(e);
    if (!m || e[e.length - 1] !== ')') return false;
    let d = 0;
    let q = false;
    for (let i = m[0].length - 1; i < e.length; i++) {
        const c = e[i];
        if (c === '"' && e[i - 1] !== '\\') q = !q;
        if (q) continue;
        if (c === '(') d++;
        if (c === ')') { d--; if (d === 0) return i === e.length - 1; }
    }
    return false;
};
const wrap = e => (isAtomic(e) ? e : `(${e})`);

const catOf = op => {
    const p = String(op).split('_')[0];
    return ['motion', 'looks', 'sound', 'event', 'control', 'sensing', 'operator', 'data', 'procedures', 'pen'].indexOf(p) >= 0 ? p : 'other';
};

/**
 * @param {object} target 스크래치 스프라이트(RenderedTarget) — blocks·getName 사용
 * @param {string} lang 'ko' | 'en'
 * @returns {{code: string, converted: number, skipped: number}} 파이썬 코드와 바꾼/못 바꾼 블록 수
 */
const blocksToPython = (target, lang) => {
    const L = T[lang] || T.en;
    const bl = target.blocks;
    const get = id => (id ? bl.getBlock(id) : null);
    let converted = 0;
    let skipped = 0;
    const names = {};
    const used = new Set([L.store]);
    // 변수·함수 이름: 파이썬에서 쓸 수 있게(한글 가능 — 파이썬 탭 실측), 겹치면 뒤에 번호
    const pyName = (raw, kind) => {
        const key = `${kind}:${raw}`;
        if (names[key]) return names[key];
        // 내 블록 이름의 입력 자리(%s·%n·%b)는 이름에서 뺀다('춤 %s' → 춤)
        let n = String(raw).replace(/%[sbn]/g, ' ').trim().replace(/[^A-Za-z0-9_가-힣]+/g, '_').replace(/^_+|_+$/g, '');
        if (!n || /^\d/.test(n)) n = `${kind === 'fn' ? 'f' : 'v'}_${n}`;
        if (PY_KEYWORDS.indexOf(n) >= 0) n = `${n}_`;
        let out = n;
        let i = 2;
        while (used.has(out)) out = `${n}_${i++}`;
        used.add(out);
        names[key] = out;
        return out;
    };
    const fieldVal = (b, name) => (b && b.fields && b.fields[name] ? b.fields[name].value : '');
    // 변수: 스크래치 변수는 처음부터 값이 있다 → 쓴 변수는 맨 위에서 지금 값으로 만들어 둔다(안 하면 '정의되지 않음' 오류)
    // 코드에는 자리표(__cv0__)로 적고, 다 만든 뒤 이름 또는 변수["이름"] 으로 바꾼다(함수 안에서 바꾸는지는 끝나야 안다)
    const varsUsed = {};
    const varToken = {};
    const shared = new Set();
    let inFn = false;
    const varRef = (b, assign) => {
        const f = b.fields && b.fields.VARIABLE;
        const n = pyName(f ? f.value : 'var', 'var');
        if (!(n in varsUsed)) {
            let v = 0;
            try {
                const vr = f && f.id && target.lookupVariableById ? target.lookupVariableById(f.id) : null;
                if (vr && typeof vr.value !== 'undefined' && !Array.isArray(vr.value)) v = vr.value;
            } catch (e) { /* 없으면 0 */ }
            varsUsed[n] = v;
            varToken[n] = `__cv${Object.keys(varToken).length}__`;
        }
        if (assign && inFn) shared.add(n);
        return varToken[n];
    };

    // ── 값(식) ──
    const notesForExpr = [];
    // 입력칸이면 그 블록의 값, 드롭다운 고정 메뉴(acceptReporters 없는 메뉴 = 필드)면 그 값
    const inputExpr = (b, name, fallback) => {
        const inp = b.inputs && b.inputs[name];
        if (inp && (inp.block || inp.shadow)) return expr(inp.block || inp.shadow);
        if (b.fields && b.fields[name]) return lit(b.fields[name].value);
        return typeof fallback === 'undefined' ? 'None' : fallback;
    };
    const expr = id => {
        const b = get(id);
        if (!b) return 'None';
        switch (b.opcode) {
        case 'math_number': case 'math_positive_number': case 'math_whole_number': case 'math_integer': case 'math_angle':
            return lit(fieldVal(b, 'NUM'));
        case 'text': return lit(fieldVal(b, 'TEXT'));
        case 'colour_picker': return quote(fieldVal(b, 'COLOUR'));
        case 'matrix8x8': case 'matrix': {
            const v = fieldVal(b, Object.keys(b.fields)[0]);
            return quote(matrixHex(v) || v);
        }
        case 'data_variable': return varRef(b);
        case 'argument_reporter_string_number': case 'argument_reporter_boolean': return pyName(fieldVal(b, 'VALUE'), 'arg');
        case 'operator_add': return `${wrap(inputExpr(b, 'NUM1'))} + ${wrap(inputExpr(b, 'NUM2'))}`;
        case 'operator_subtract': return `${wrap(inputExpr(b, 'NUM1'))} - ${wrap(inputExpr(b, 'NUM2'))}`;
        case 'operator_multiply': return `${wrap(inputExpr(b, 'NUM1'))} * ${wrap(inputExpr(b, 'NUM2'))}`;
        case 'operator_divide': return `${wrap(inputExpr(b, 'NUM1'))} / ${wrap(inputExpr(b, 'NUM2'))}`;
        case 'operator_mod': return `${wrap(inputExpr(b, 'NUM1'))} % ${wrap(inputExpr(b, 'NUM2'))}`;
        case 'operator_round': return `round(${inputExpr(b, 'NUM')})`;
        case 'operator_random': return `randint(${inputExpr(b, 'FROM')}, ${inputExpr(b, 'TO')})`;
        case 'operator_gt': return `${wrap(inputExpr(b, 'OPERAND1'))} > ${wrap(inputExpr(b, 'OPERAND2'))}`;
        case 'operator_lt': return `${wrap(inputExpr(b, 'OPERAND1'))} < ${wrap(inputExpr(b, 'OPERAND2'))}`;
        case 'operator_equals': return `${wrap(inputExpr(b, 'OPERAND1'))} == ${wrap(inputExpr(b, 'OPERAND2'))}`;
        case 'operator_and': return `${wrap(inputExpr(b, 'OPERAND1', 'False'))} and ${wrap(inputExpr(b, 'OPERAND2', 'False'))}`;
        case 'operator_or': return `${wrap(inputExpr(b, 'OPERAND1', 'False'))} or ${wrap(inputExpr(b, 'OPERAND2', 'False'))}`;
        case 'operator_not': return `not (${inputExpr(b, 'OPERAND', 'False')})`;
        case 'operator_join': return `str(${inputExpr(b, 'STRING1')}) + str(${inputExpr(b, 'STRING2')})`;
        case 'operator_length': return `len(str(${inputExpr(b, 'STRING')}))`;
        // 로봇 센서
        case 'cubroidproximity_isProximity': return 'is_obstacle_detected()';
        case 'cubroidlighttouch_isButtonPressed': return 'is_button_pressed()';
        case 'cubroidlighttouch_getBrightness': return 'get_brigtness()';
        default: {
            // 메뉴 그림자 블록(드롭다운) — 첫 칸 값
            if (b.shadow && b.fields && Object.keys(b.fields).length) return lit(fieldVal(b, Object.keys(b.fields)[0]));
            notesForExpr.push(L.unsupportedExpr(b.opcode));
            return 'None';
        }
        }
    };
    // 조건: 맨 바깥 괄호 없이(jspython)
    const cond = (b, name) => {
        const inp = b.inputs && b.inputs[name];
        if (!inp || !(inp.block || inp.shadow)) return 'False';
        return expr(inp.block || inp.shadow);
    };

    // ── 문장 ──
    const pad = n => '    '.repeat(n);
    const loopVars = ['i', 'j', 'k', 'm', 'n'];
    const stack = (id, ind, depth) => {
        const out = [];
        let cur = id;
        while (cur) {
            const b = get(cur);
            if (!b) break;
            out.push(...stmt(b, ind, depth));
            cur = b.next;
        }
        return out;
    };
    const body = (b, name, ind, depth) => {
        const inp = b.inputs && b.inputs[name];
        const lines = inp && inp.block ? stack(inp.block, ind, depth) : [];
        // 비었거나 주석뿐이면 pass(파이썬 문법)
        if (!lines.some(l => !/^\s*#/.test(l))) lines.push(`${pad(ind)}pass`);
        return lines;
    };
    // 반복(무한·까지)은 한 바퀴마다 잠깐 쉰다: 스크래치도 한 바퀴마다 화면을 그리며 쉬고,
    // 안 쉬면 계산만 하는 반복이 브라우저를 멈추고 [정지]도 안 듣는다(시험에서 실제로 멈춤)
    const loopBody = (b, ind, depth) => {
        const lines = body(b, 'SUBSTACK', ind, depth).filter(l => l !== `${pad(ind)}pass`);
        if (!lines.some(l => l.indexOf(`${pad(ind)}sleep(`) === 0)) lines.push(`${pad(ind)}sleep(0.01)`);
        return lines;
    };
    const motorArg = v => (/^"(left|right|stop)"$/i.test(v) ? v.toUpperCase() : v);
    const robot = (b, ind) => {
        const p = pad(ind);
        const op = b.opcode;
        const m = /^cubroiddcmotor0([12])_(\w+)$/.exec(op);
        if (m) {
            const n = m[1];
            if (m[2] === 'dcMotorControl') return [`${p}turn_dcmotor${n}(${motorArg(inputExpr(b, 'INDEX'))})`];
            if (m[2] === 'dcMotorControl2') return [`${p}turn_dcmotor${n}_time(${motorArg(inputExpr(b, 'INDEX'))}, ${inputExpr(b, 'TIME')})`];
            if (m[2] === 'dcMotorStop') return [`${p}turn_dcmotor${n}("STOP")`];
            if (m[2] === 'dcMotorSetSpeed') {
                const s = inputExpr(b, 'SPEED');
                // 블록은 0~100%, 파이썬은 0~255
                return [`${p}set_dcmotor${n}_speed(${isNum(s) ? Math.round(Math.max(0, Math.min(100, Number(s))) * 255 / 100) : `round(${wrap(s)} * 255 / 100)`})`];
            }
        }
        switch (op) {
        case 'cubroidmaster_cubroidMasterColorControl': return [`${p}turnon_colorlight(${inputExpr(b, 'COLOR')})`];
        case 'cubroidmaster_cubroidColorOffControl': return [`${p}turnoff_colorlight()`];
        case 'cubroidled_lceMatrix8x8Control': case 'cubroidled_lceAlpabetControl': case 'cubroidled_lceNumberControl': case 'cubroidled_lceImgControl':
            return [`${p}display_set_pixel(${inputExpr(b, 'MATRIX')})`];
        case 'cubroidled_ledAllClearControl': return [`${p}display_clear()`];
        case 'cubroidsound_soundControl': case 'cubroidsound_soundBeatControl': {
            const raw = inputExpr(b, 'SOUND');
            const k = /^"(.*)"$/.test(raw) ? noteKey(JSON.parse(raw)) : null;
            const arg = k ? quote(k) : raw;
            return op === 'cubroidsound_soundControl' ? [`${p}play_note(${arg})`] : [`${p}play_note_time(${arg}, ${inputExpr(b, 'BEATS')})`];
        }
        default: return null;
        }
    };
    const stmt = (b, ind, depth) => {
        const p = pad(ind);
        const before = notesForExpr.length;
        const withNotes = lines => {
            const extra = notesForExpr.splice(before).map(n => `${p}# ${n}`);
            return extra.concat(lines);
        };
        const r = robot(b, ind);
        if (r) { converted++; return withNotes(r); }
        switch (b.opcode) {
        case 'control_wait': converted++; return withNotes([`${p}sleep(${inputExpr(b, 'DURATION')})`]);
        case 'control_repeat': {
            converted++;
            const v = loopVars[Math.min(depth, loopVars.length - 1)];
            const t = inputExpr(b, 'TIMES');
            const head = `${p}for ${v} in range(${isNum(t) ? Math.max(0, Math.round(Number(t))) : `int(${t})`}):`;
            return withNotes([head].concat(body(b, 'SUBSTACK', ind + 1, depth + 1)));
        }
        case 'control_forever':
            converted++;
            return withNotes([`${p}while True:`].concat(loopBody(b, ind + 1, depth + 1)));
        case 'control_if':
            converted++;
            return withNotes([`${p}if ${cond(b, 'CONDITION')}:`].concat(body(b, 'SUBSTACK', ind + 1, depth)));
        case 'control_if_else':
            converted++;
            return withNotes([`${p}if ${cond(b, 'CONDITION')}:`].concat(body(b, 'SUBSTACK', ind + 1, depth), [`${p}else:`], body(b, 'SUBSTACK2', ind + 1, depth)));
        case 'control_repeat_until':
            converted++;
            return withNotes([`${p}while not (${cond(b, 'CONDITION')}):`].concat(loopBody(b, ind + 1, depth + 1)));
        case 'control_wait_until':
            converted++;
            return withNotes([`${p}while not (${cond(b, 'CONDITION')}):`, `${pad(ind + 1)}sleep(0.05)`]);
        case 'control_stop': converted++; return [`${p}${L.stop} (${fieldVal(b, 'STOP_OPTION')})`];
        case 'data_setvariableto': converted++; return withNotes([`${p}${varRef(b, true)} = ${inputExpr(b, 'VALUE')}`]);
        case 'data_changevariableby': {
            converted++;
            const v = varRef(b, true);
            return withNotes([`${p}${v} = ${v} + ${wrap(inputExpr(b, 'VALUE'))}`]);
        }
        case 'looks_say': case 'looks_think': converted++; return withNotes([`${p}print(${inputExpr(b, 'MESSAGE')})`]);
        case 'looks_sayforsecs': case 'looks_thinkforsecs':
            converted++;
            return withNotes([`${p}print(${inputExpr(b, 'MESSAGE')})`, `${p}sleep(${inputExpr(b, 'SECS')})`]);
        case 'event_broadcast': case 'event_broadcastandwait': {
            converted++;
            const msgB = get(b.inputs.BROADCAST_INPUT && (b.inputs.BROADCAST_INPUT.block || b.inputs.BROADCAST_INPUT.shadow));
            const msg = msgB ? fieldVal(msgB, 'BROADCAST_OPTION') : '';
            // 받는 블록이 없으면 부를 함수도 없다(부르면 '정의되지 않음' 오류)
            if (!receivers.has(msg)) return [`${p}${L.noReceiver(msg)}`];
            return [`${p}${pyName(`when_${msg}`, 'fn')}()`];
        }
        case 'procedures_call': {
            converted++;
            const mut = b.mutation || {};
            if (!procs.has(mut.proccode)) return [`${p}${L.noProc(String(mut.proccode || '').replace(/%[sbn]/g, '').trim())}`];
            const ids = JSON.parse(mut.argumentids || '[]');
            const args = ids.map(aid => inputExpr(b, aid));
            return withNotes([`${p}${pyName(mut.proccode || 'block', 'fn')}(${args.join(', ')})`]);
        }
        default:
            skipped++;
            notesForExpr.splice(before);
            return [`${p}${L.unsupported(L.cats[catOf(b.opcode)], b.opcode)}`];
        }
    };

    // ── 스크립트 묶기 ──
    const flag = [];
    const flagLoops = []; // 깃발 스크립트 중 무한 반복으로 끝나는 것: {n, pre, body}
    const sensors = [];
    const funcs = [];
    const loose = [];
    const SENSOR_HATS = {
        cubroidproximity_whenTheDistanceIsClose: () => 'is_obstacle_detected()',
        cubroidlighttouch_whenButtonPressed: () => 'is_button_pressed()',
        cubroidlighttouch_whenBrightnessLessThan01: b => `get_brigtness() < ${wrap(inputExpr(b, 'DISTANCE'))}`,
        cubroidlighttouch_whenBrightnessLessThan02: b => `get_brigtness() > ${wrap(inputExpr(b, 'DISTANCE'))}`
    };
    const scripts = bl.getScripts().map(get).filter(Boolean)
        .sort((a, b) => ((a.y || 0) - (b.y || 0)) || ((a.x || 0) - (b.x || 0)));
    const protoOf = top => get(top.inputs.custom_block && (top.inputs.custom_block.shadow || top.inputs.custom_block.block));
    // 신호를 받는 블록·내 블록 정의가 있는지 먼저 본다(없는 함수를 부르지 않게)
    const receivers = new Set(scripts.filter(t => t.opcode === 'event_whenbroadcastreceived').map(t => fieldVal(t, 'BROADCAST_OPTION')));
    const procs = new Set(scripts.filter(t => t.opcode === 'procedures_definition')
        .map(t => ((protoOf(t) || {}).mutation || {}).proccode).filter(Boolean));
    scripts.forEach(top => {
        const op = top.opcode;
        if (op === 'event_whenflagclicked') {
            converted++;
            // 무한 반복으로 끝나면 앞부분과 반복 몸통을 나눠 둔다 → 끝나는 스크립트를 먼저, 무한 반복들은 맨 아래 한 반복으로
            // (차례로만 두면 첫 무한 반복 뒤의 스크립트가 영영 실행되지 않는다)
            const ids = [];
            for (let c = top.next; c && get(c); c = get(c).next) ids.push(c);
            const last = ids.length ? get(ids[ids.length - 1]) : null;
            if (last && last.opcode === 'control_forever') {
                converted++;
                const pre = [];
                ids.slice(0, -1).forEach(i => pre.push(...stmt(get(i), 0, 0)));
                const lines = body(last, 'SUBSTACK', 1, 1).filter(l => l !== '    pass');
                flagLoops.push({n: flag.length + flagLoops.length + 1, pre, body: lines});
            } else flag.push(stack(top.next, 0, 0));
            return;
        }
        if (SENSOR_HATS[op]) { converted++; sensors.push({cond: SENSOR_HATS[op](top), body: top.next ? stack(top.next, 2, 1) : []}); return; }
        if (op === 'procedures_definition') {
            converted++;
            const mut = (protoOf(top) || {}).mutation || {};
            const argNames = JSON.parse(mut.argumentnames || '[]').map(a => pyName(a, 'arg'));
            inFn = true;
            const lines = top.next ? stack(top.next, 1, 0) : [];
            inFn = false;
            if (!lines.some(l => !/^\s*#/.test(l))) lines.push('    pass');
            funcs.push([`def ${pyName(mut.proccode || 'block', 'fn')}(${argNames.join(', ')}):`].concat(lines));
            return;
        }
        if (/^event_when/.test(op) || top.opcode.indexOf('_when') > 0) {
            converted++;
            let name = op;
            let label = op;
            if (op === 'event_whenbroadcastreceived') { const msg = fieldVal(top, 'BROADCAST_OPTION'); name = `when_${msg}`; label = msg; }
            else if (op === 'event_whenkeypressed') { name = `when_key_${fieldVal(top, 'KEY_OPTION')}`; label = `${fieldVal(top, 'KEY_OPTION')} key`; }
            else if (op === 'event_whenthisspriteclicked') { name = 'when_clicked'; }
            inFn = true;
            const lines = top.next ? stack(top.next, 1, 0) : [];
            inFn = false;
            if (!lines.some(l => !/^\s*#/.test(l))) lines.push('    pass');
            funcs.push([op === 'event_whenbroadcastreceived' ? null : L.eventFn(label), `def ${pyName(name, 'fn')}():`].filter(Boolean).concat(lines));
            return;
        }
        // 모자 블록 없이 떨어져 있는 블록 묶음 — 스크래치에서는 저절로 안 돈다
        loose.push(stack(top.id, 0, 0).map(l => `# ${l}`));
    });

    const out = [L.head(target.getName ? target.getName() : 'sprite'), ''];
    if (!flag.length && !flagLoops.length && !sensors.length && !funcs.length) {
        out.push(L.noScripts, 'pass');
        return {code: `${out.join('\n')}\n`, converted: 0, skipped: 0};
    }
    const vnames = Object.keys(varsUsed);
    const plain = vnames.filter(n => !shared.has(n));
    const box = vnames.filter(n => shared.has(n));
    plain.forEach(n => out.push(`${n} = ${lit(varsUsed[n])}`));
    if (box.length) {
        out.push(L.storeNote, `${L.store} = {${box.map(n => `${quote(n)}: ${lit(varsUsed[n])}`).join(', ')}}`);
    }
    if (vnames.length) out.push('');
    funcs.forEach(f => { out.push(...f, ''); });
    if (flag.length + flagLoops.length > 1) out.push(L.flagMany);
    flag.forEach((lines, i) => {
        if (i > 0 && lines.length) out.push('');
        out.push(...lines);
    });
    // 무한 반복(깃발 스크립트 것 + 센서 모자 확인)은 맨 아래 한 반복으로
    if (flagLoops.length) {
        if (flag.length) out.push('');
        if (flag.length || flagLoops.length > 1) out.push(L.loopsLast);
        flagLoops.forEach(fl => out.push(...fl.pre));
        out.push('while True:');
        flagLoops.forEach(fl => {
            if (flagLoops.length > 1) out.push(`    ${L.loopFrom(fl.n)}`);
            out.push(...fl.body);
        });
    }
    if (sensors.length) {
        if (flagLoops.length) out.push(`    ${L.sensorInLoop}`);
        else out.push('', L.sensorLoop, 'while True:');
        sensors.forEach(sv => {
            out.push(`    if ${sv.cond}:`);
            out.push(...(sv.body.some(l => !/^\s*#/.test(l)) ? sv.body : sv.body.concat(['        pass'])));
        });
    }
    // 반복은 한 바퀴마다 잠깐 쉰다(loopBody 와 같은 까닭). 몸통이 비면 이것이 유일한 줄
    if (flagLoops.length || sensors.length) {
        const loopStart = out.lastIndexOf('while True:');
        if (!out.slice(loopStart + 1).some(l => /^ {4}sleep\(/.test(l))) out.push(sensors.length && !flagLoops.length ? '    sleep(0.1)' : '    sleep(0.01)');
    }
    if (loose.length) { out.push(''); loose.forEach(l => out.push(...l)); }
    // 주석뿐이면 파이썬 탭 엔진이 오류를 낸다(실측) → 실행할 한 줄
    if (!out.some(l => l.trim() && !/^\s*#/.test(l))) out.push('pass');
    if (skipped) out.splice(1, 0, L.headNote);
    // 변수 자리표 → 이름 / 변수["이름"]
    let code = `${out.join('\n')}\n`;
    vnames.forEach(n => {
        code = code.split(varToken[n]).join(shared.has(n) ? `${L.store}[${quote(n)}]` : n);
    });
    return {code, converted, skipped};
};

module.exports = {blocksToPython, noteKey, matrixHex};
